import { useRef, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote,
  Link as LinkIcon, Image as ImageIcon, Undo2, Redo2, Loader2, Minus,
} from "lucide-react";

function Btn({ onClick, active, disabled, title, children, testid }) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled} title={title} data-testid={testid}
      className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors disabled:opacity-40 ${
        active ? "bg-navy text-white" : "text-navy hover:bg-navy/10"
      }`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, postId }) {
  const fileRef = useRef(null);
  const uploadingRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Image.configure({ HTMLAttributes: { class: "rounded-xl my-3 w-full" } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "prose-editor focus:outline-none min-h-[220px] px-4 py-3" } },
  });

  // Sincroniza el contenido cuando cambia la noticia (evita mostrar el post anterior)
  useEffect(() => {
    if (!editor) return;
    const incoming = value || "";
    if (incoming !== editor.getHTML()) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Enlace (URL):", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const pickImage = () => {
    if (!postId) { toast.error("Guardá la noticia primero para poder insertar imágenes"); return; }
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploadingRef.current) return;
    uploadingRef.current = true;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/posts/${postId}/inline-image`, fd);
      editor.chain().focus().setImage({ src: data.url }).run();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir la imagen"); }
    finally { uploadingRef.current = false; if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-100 bg-[#f7f8fc] px-2 py-1.5">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Negrita" testid="rte-bold"><Bold className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Cursiva" testid="rte-italic"><Italic className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-gray-200 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Título" testid="rte-h2"><Heading2 className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subtítulo" testid="rte-h3"><Heading3 className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Lista" testid="rte-ul"><List className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Lista numerada" testid="rte-ol"><ListOrdered className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Cita" testid="rte-quote"><Quote className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separador" testid="rte-hr"><Minus className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-gray-200 mx-1" />
        <Btn onClick={addLink} active={editor.isActive("link")} title="Enlace" testid="rte-link"><LinkIcon className="h-4 w-4" /></Btn>
        <Btn onClick={pickImage} title="Insertar imagen" testid="rte-image"><ImageIcon className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-gray-200 mx-1" />
        <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Deshacer" testid="rte-undo"><Undo2 className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rehacer" testid="rte-redo"><Redo2 className="h-4 w-4" /></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="rte-image-input" />
      </div>
      <EditorContent editor={editor} data-testid="rte-content" />
    </div>
  );
}
